import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { Card, CardContent } from "@/components/ui/card";
import aboutTeam from "@/assets/about-team.jpg";

const About = () => (
  <Layout>
    <SEOHead title="About Xentic Solutions | AI Automation for Small Business" description="We're a lean team of automation specialists on a mission to make AI accessible to every small business." path="/about" />

    <section className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute top-1/3 -right-32 w-96 h-96 bg-accent/15 rounded-full blur-[120px] animate-blob" />
      <div className="container text-center max-w-3xl relative z-10">
        <ScrollReveal>
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">About Us</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold">We're Not a Big Agency. <span className="text-gradient">That's the Point.</span></h1>
          <p className="mt-6 text-lg text-muted-foreground">We're a focused team of AI and automation specialists. No bloated teams, no inflated invoices, just results.</p>
        </ScrollReveal>
      </div>
    </section>

    <section className="py-12">
      <div className="container max-w-3xl">
        <ScrollReveal>
          <div className="rounded-2xl overflow-hidden shadow-lg border border-border">
            <img src={aboutTeam} alt="The Xentic Solutions team" className="w-full h-auto object-cover" />
          </div>
        </ScrollReveal>
      </div>
    </section>

    <section className="py-20 section-alt">
      <div className="container max-w-3xl">
        <ScrollReveal>
          <h2 className="font-heading text-3xl font-bold mb-6">Our Story</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">It started with frustration.</p>
          <p className="text-muted-foreground leading-relaxed mb-4">We kept watching small business owners work themselves to the ground answering the same questions manually, losing leads because they could not respond fast enough, and missing out on growth because they simply did not have the time or team to keep up.</p>
          <p className="text-muted-foreground leading-relaxed mb-4">Then we saw the quotes they were getting from agencies. Thousands of dollars for tools that should cost a fraction of that. Complex retainers. Vague deliverables. And at the end of it still no real results.</p>
          <p className="text-muted-foreground leading-relaxed">It did not sit right with us.</p>
        </ScrollReveal>
      </div>
    </section>

    <section className="py-20">
      <div className="container max-w-3xl">
        <ScrollReveal>
          <h2 className="font-heading text-3xl font-bold mb-6">Mission & Vision</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-heading font-bold mb-2">Mission</h3>
                <p className="text-sm text-muted-foreground">To make AI automation accessible, affordable, and effective for every small business, regardless of budget or technical skill.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <h3 className="font-heading font-bold mb-2">Vision</h3>
                <p className="text-sm text-muted-foreground">A world where every small business has the same AI capabilities as enterprise companies, at a fraction of the cost.</p>
              </CardContent>
            </Card>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <section className="py-20 section-alt">
      <div className="container max-w-3xl">
        <ScrollReveal>
          <h2 className="font-heading text-3xl font-bold mb-8">What Makes Us Different</h2>
        </ScrollReveal>
        <div className="space-y-4">
          {[
            { title: "Transparent", desc: "No hidden fees, no surprise invoices. What we quote is what you pay." },
            { title: "Fast", desc: "Most projects go live in 72 to 120 hours. No weeks of waiting." },
            { title: "Risk-Free", desc: "30-day money-back guarantee on every project." },
            { title: "Industry Knowledge", desc: "We've built solutions for HVAC, healthcare, legal, real estate, and more." },
            { title: "Partners, Not Vendors", desc: "We work with you long-term. Your growth is our growth." },
          ].map((d, i) => (
            <ScrollReveal key={i} delay={i * 0.05}>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-heading font-bold mb-1">{d.title}</h3>
                  <p className="text-sm text-muted-foreground">{d.desc}</p>
                </CardContent>
              </Card>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20">
      <div className="container max-w-md">
        <ScrollReveal>
          <h2 className="font-heading text-2xl font-bold mb-6 text-center">Company Info</h2>
          <div className="rounded-xl overflow-hidden glass-card">
            <table className="w-full text-sm">
              <tbody>
                {[
                  ["Company", "Xentic Solutions"],
                  ["Parent Company", "Skipper Ventures LLC"],
                  ["Location", "107 Williamsburg Lane, Bolingbrook, IL 60440"],
                  ["Phone (Call)", "+1 (872) 297-0607"],
                  ["Email", "xenticsolutions.com / info@xenticsolutions.com"],
                ].map(([k, v], i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-secondary/30" : ""}>
                    <td className="p-4 font-medium">{k}</td>
                    <td className="p-4 text-muted-foreground">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ScrollReveal>
      </div>
    </section>
  </Layout>
);

export default About;
